// What the device says about itself, for the benchmark report's `device` block.
//
// ## Why a facets seam rather than reading `navigator` directly
//
// The same reason `practice-capability.ts` gives for its own probe: a direct `navigator` read is
// untestable, and every facet here is optional in at least one shipped browser. Expressing the
// read as an injected `DeviceFacets` object means the *assembly* is unit-testable against fixed
// facets, while `browserDeviceFacets()` is the one place that touches globals.
//
// ## Why every facet is nullable, and why nothing is defaulted
//
// `navigator.deviceMemory` is Chromium-only; `navigator.platform` is deprecated and absent or
// frozen in some engines; a headless or embedded context may have no `screen` at all. The report
// is a MEASUREMENT RECORD, so an absent facet is recorded as `null` — never as `0`, never as
// `'unknown'`, and never omitted. A zero that means "not reported" is indistinguishable from a
// zero that means zero once it is in a JSONL file, and the reader of that file will not have the
// device any more to ask.
//
// Nothing here is used to gate behaviour. It exists so that two rows in the results file can be
// told apart by a human.

import type { DeviceDetails } from './report';

/**
 * The globals this module reads, named explicitly so a test can supply them.
 *
 * Every member is optional because every member is genuinely absent somewhere.
 */
export interface DeviceFacets {
  readonly navigator?: {
    readonly userAgent?: unknown;
    readonly platform?: unknown;
    readonly hardwareConcurrency?: unknown;
    /** Chromium-only, in GiB, and coarsened by the browser for fingerprinting reasons. */
    readonly deviceMemory?: unknown;
    readonly language?: unknown;
  };
  readonly screen?: {
    readonly width?: unknown;
    readonly height?: unknown;
  };
  readonly devicePixelRatio?: unknown;
}

/** A string facet, or `null` if it is absent or not actually a string. */
function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * A finite positive number facet, or `null`.
 *
 * Non-finite is rejected as well as non-numeric: a `devicePixelRatio` of `Infinity` is not a
 * display characteristic, and it would serialise to `null` in JSON anyway — better to record
 * that deliberately than to discover it downstream.
 */
function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

/** Reads the device's self-description from the supplied facets. Pure. */
export function readDeviceDetails(facets: DeviceFacets): DeviceDetails {
  const nav = facets.navigator;
  const screen = facets.screen;
  return {
    userAgent: str(nav?.userAgent),
    platform: str(nav?.platform),
    hardwareConcurrency: num(nav?.hardwareConcurrency),
    deviceMemoryGb: num(nav?.deviceMemory),
    screenWidth: num(screen?.width),
    screenHeight: num(screen?.height),
    devicePixelRatio: num(facets.devicePixelRatio),
    language: str(nav?.language),
  };
}

/**
 * The real browser's facets. The **only** place this module touches a global, and it reads them
 * defensively: a non-browser context (a node test project, a worker) simply yields an all-`null`
 * `DeviceDetails` rather than throwing.
 */
export function browserDeviceFacets(): DeviceFacets {
  const g = globalThis as {
    navigator?: DeviceFacets['navigator'];
    screen?: DeviceFacets['screen'];
    devicePixelRatio?: unknown;
  };
  return {
    navigator: g.navigator,
    screen: g.screen,
    devicePixelRatio: g.devicePixelRatio,
  };
}
